import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, CardBody, Container, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { requestJson } from "../../../helpers/api";
import {
  DENOMINACIONES,
  DETALLE_TARJETA_PROVEEDORES,
  FORMAS_EXCLUIDAS,
  TOTAL_DIGITOS_FACTURA,
  WIZARD_STEPS,
} from "./constants";
import {
  construirClaveSucursal,
  construirIdFacturaInput,
  crearArticuloGasto,
  crearFacturaSegmentoVacio,
  crearMapaDenominaciones,
  formatearFacturaSegmento,
  obtenerIndicePaso,
} from "./helpers";
import {
  BancoReferencia,
  BoletaForm,
  CajaDiaAnterior,
  CuentaBancoReferencia,
  FacturaAnulada,
  FacturaSegmento,
  FormaPagoDisponible,
  GastoForm,
  GastoItemForm,
  RendicionExistente,
  ResumenEstado,
  SucursalRendicion,
} from "./types";
import StepLayout from "./components/StepLayout";
import StepArrowNav from "./components/StepArrowNav";
import SeleccionStep from "./components/SeleccionStep";
import FacturasRangoStep from "./components/FacturasRangoStep";
import FacturasAnuladasStep from "./components/FacturasAnuladasStep";
import FondoStep from "./components/FondoStep";
import GastosStep from "./components/GastosStep";
import PagosStep from "./components/PagosStep";
import BoletasStep from "./components/BoletasStep";
import ConfirmacionStep from "./components/ConfirmacionStep";
import EfectivoDetalleModal from "./components/EfectivoDetalleModal";
import TarjetaDetalleModal from "./components/TarjetaDetalleModal";

const RendicionCajaPage = () => {
  const [pasoActual, setPasoActual] = useState(0);
  const [maxPasoAlcanzado, setMaxPasoAlcanzado] = useState(0);
  const [guardandoPaso, setGuardandoPaso] = useState(false);
  const [guardandoConfirmacion, setGuardandoConfirmacion] = useState(false);
  const [errorPaso, setErrorPaso] = useState<string | null>(null);
  const [modalExito, setModalExito] = useState(false);

  const [sucursales, setSucursales] = useState<SucursalRendicion[]>([]);
  const [cargandoSucursales, setCargandoSucursales] = useState(false);
  const [errorSucursales, setErrorSucursales] = useState<string | null>(null);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState<SucursalRendicion | null>(null);

  const [cajasDiaAnterior, setCajasDiaAnterior] = useState<CajaDiaAnterior[]>([]);
  const [cargandoCajas, setCargandoCajas] = useState(false);
  const [errorCajas, setErrorCajas] = useState<string | null>(null);
  const [cajaSeleccionadaId, setCajaSeleccionadaId] = useState<number | null>(null);

  const [formasPago, setFormasPago] = useState<FormaPagoDisponible[]>([]);
  const [cargandoFormasPago, setCargandoFormasPago] = useState(false);
  const [errorFormasPago, setErrorFormasPago] = useState<string | null>(null);
  const [pagosRegistrados, setPagosRegistrados] = useState<Record<number, string>>({});
  const [notasFormasPago, setNotasFormasPago] = useState<Record<number, string>>({});
  const [detalleEfectivoFormas, setDetalleEfectivoFormas] = useState<Record<number, Record<number, string>>>({});
  const [detalleTarjetaFormas, setDetalleTarjetaFormas] = useState<Record<number, Record<string, string>>>({});

  const [bancos, setBancos] = useState<BancoReferencia[]>([]);
  const [cuentasBanco, setCuentasBanco] = useState<Record<number, CuentaBancoReferencia[]>>({});
  const [cargandoBancos, setCargandoBancos] = useState(false);
  const [cargandoCuentas, setCargandoCuentas] = useState<Record<number, boolean>>({});
  const [rendicionesExistentes, setRendicionesExistentes] = useState<Record<number, RendicionExistente>>({});
  const [modalEfectivo, setModalEfectivo] = useState<{ abierta: boolean; forma: FormaPagoDisponible | null }>({
    abierta: false,
    forma: null,
  });
  const [modalTarjeta, setModalTarjeta] = useState<{
    abierta: boolean;
    forma: FormaPagoDisponible | null;
    tipo: "credito" | "debito" | null;
  }>({
    abierta: false,
    forma: null,
    tipo: null,
  });

  const [denominaciones, setDenominaciones] = useState<Record<number, string>>(crearMapaDenominaciones);
  const [notaFondos, setNotaFondos] = useState("");
  const [confirmarCajaModal, setConfirmarCajaModal] = useState<{
    visible: boolean;
    caja: CajaDiaAnterior | null;
    mensaje: string;
  }>({ visible: false, caja: null, mensaje: "" });
  const [confirmarPasoModal, setConfirmarPasoModal] = useState<{
    visible: boolean;
    mensaje: string;
    confirmarTexto?: string;
    onConfirm?: (() => void) | null;
  }>({ visible: false, mensaje: "", confirmarTexto: undefined, onConfirm: null });
  const [inicioRendicionTimestamp, setInicioRendicionTimestamp] = useState<number | null>(null);
  const [confirmacionTiempoRestante, setConfirmacionTiempoRestante] = useState<number | null>(null);
  const [confirmacionTemporizadorActivo, setConfirmacionTemporizadorActivo] = useState(false);
  const [avisoTemporizadorConfirmacionMostrado, setAvisoTemporizadorConfirmacionMostrado] = useState(false);
  const confirmacionTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [confirmacionTimeoutModal, setConfirmacionTimeoutModal] = useState(false);
  const [confirmacionIntentosFallidos, setConfirmacionIntentosFallidos] = useState(0);
  const ultimaCajaRegistradaRef = useRef<number | null>(null);
  const crearGastoBase = useCallback((): GastoForm => {
    return {
      id: Date.now(),
      descripcion: "",
      proveedor: "",
      monto: 0,
      tipo: "operativo",
      compraTipo: "contado",
      ruc: "",
      numero: "",
      fecha: "",
      nota: "",
      articulos: [crearArticuloGasto()],
    };
  }, []);
  const [gastos, setGastos] = useState<GastoForm[]>([]);
  const [nuevoGasto, setNuevoGasto] = useState<GastoForm>(() => crearGastoBase());
  const [boletas, setBoletas] = useState<BoletaForm[]>([]);
  const [nuevaBoleta, setNuevaBoleta] = useState<BoletaForm>({
    id: Date.now(),
    fecha: "",
    numero: "",
    banco: "",
    cuenta: "",
    monto: 0,
  });
  const [facturaDesde, setFacturaDesde] = useState<FacturaSegmento>(() => crearFacturaSegmentoVacio());
  const [facturaHasta, setFacturaHasta] = useState<FacturaSegmento>(() => crearFacturaSegmentoVacio());
  const [facturasAnuladas, setFacturasAnuladas] = useState<FacturaAnulada[]>([]);
  const [nuevaFacturaAnulada, setNuevaFacturaAnulada] = useState<FacturaAnulada>({
    id: Date.now(),
    establecimiento: "",
    punto: "",
    numero: "",
    motivo: "",
  });

  const [resumen, setResumen] = useState<ResumenEstado | null>(null);
  const [rendicionId, setRendicionId] = useState<number | null>(null);
  const [rendicionConfirmada, setRendicionConfirmada] = useState(false);
  const pasoActualId = WIZARD_STEPS[pasoActual]?.id ?? null;
  const timelineSteps = useMemo(
    () => WIZARD_STEPS.map((step, index) => ({ ...step, clickable: index <= maxPasoAlcanzado })),
    [maxPasoAlcanzado],
  );
  const [vistaPreviaHtml, setVistaPreviaHtml] = useState<string | null>(null);

  const usuarioActual = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("authUser");
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      return {
        nombre: parsed?.usuario?.nombreUsuario ?? parsed?.username ?? parsed?.email ?? "",
        documento: parsed?.usuario?.numeroDocumento ?? parsed?.documento ?? "",
      };
    } catch {
      return null;
    }
  }, []);

  const detalleEfectivoSeleccionado = useMemo(() => {
    if (!modalEfectivo.forma) {
      return {} as Record<number, string>;
    }
    return detalleEfectivoFormas[modalEfectivo.forma.id] ?? {};
  }, [detalleEfectivoFormas, modalEfectivo.forma]);

  const totalDetalleEfectivoSeleccionado = useMemo(() => {
    if (!modalEfectivo.forma) {
      return 0;
    }
    return DENOMINACIONES.reduce(
      (acc, denominacion) => acc + denominacion * Number(detalleEfectivoSeleccionado[denominacion] ?? "0"),
      0,
    );
  }, [detalleEfectivoSeleccionado, modalEfectivo.forma]);

  const detalleTarjetaSeleccionado = useMemo(() => {
    if (!modalTarjeta.forma) {
      return {} as Record<string, string>;
    }
    return detalleTarjetaFormas[modalTarjeta.forma.id] ?? {};
  }, [detalleTarjetaFormas, modalTarjeta.forma]);

  const totalDetalleTarjetaSeleccionado = useMemo(() => {
    if (!modalTarjeta.forma) {
      return 0;
    }
    return DETALLE_TARJETA_PROVEEDORES.reduce(
      (acc, proveedor) => acc + Number(detalleTarjetaSeleccionado[proveedor] ?? "0"),
      0,
    );
  }, [detalleTarjetaSeleccionado, modalTarjeta.forma]);


  const reiniciarFlujo = useCallback((opciones?: { conservarBancos?: boolean }) => {
    setDenominaciones(crearMapaDenominaciones());
    setNotaFondos("");
    setGastos([]);
    setPagosRegistrados({});
    setNotasFormasPago({});
    setDetalleEfectivoFormas({});
    setDetalleTarjetaFormas({});
    if (!opciones?.conservarBancos) {
      setCargandoCuentas({});
      setBancos([]);
      setCuentasBanco({});
    }
    setBoletas([]);
    setFacturaDesde(crearFacturaSegmentoVacio());
    setFacturaHasta(crearFacturaSegmentoVacio());
    setFacturasAnuladas([]);
    setNuevaFacturaAnulada({
      id: Date.now(),
      establecimiento: "",
      punto: "",
      numero: "",
      motivo: "",
    });
    setResumen(null);
    setRendicionId(null);
    setPasoActual(0);
    setMaxPasoAlcanzado(0);
    setModalEfectivo({ abierta: false, forma: null });
    setModalTarjeta({ abierta: false, forma: null, tipo: null });
    setVistaPreviaHtml(null);
    setErrorPaso(null);
    setRendicionConfirmada(false);
    setConfirmacionTemporizadorActivo(false);
    setConfirmarPasoModal({ visible: false, mensaje: "", confirmarTexto: undefined, onConfirm: null });
    setConfirmacionTiempoRestante(null);
    setConfirmacionTimeoutModal(false);
    if (confirmacionTimerRef.current) {
      clearInterval(confirmacionTimerRef.current);
      confirmacionTimerRef.current = null;
    }
    setAvisoTemporizadorConfirmacionMostrado(false);
    setInicioRendicionTimestamp(null);
    ultimaCajaRegistradaRef.current = null;
  }, []);

  const cargarSucursales = useCallback(async () => {
    setCargandoSucursales(true);
    setErrorSucursales(null);
    try {
      const data = await requestJson<SucursalRendicion[]>("/api/tesoreria/mis-sucursales");
      setSucursales(Array.isArray(data) ? data : []);
    } catch (error) {
      setErrorSucursales(error instanceof Error ? error.message : "No se pudieron cargar las sucursales.");
    } finally {
      setCargandoSucursales(false);
    }
  }, []);

  const cargarCajas = useCallback(async () => {
    if (!sucursalSeleccionada) {
      setCajasDiaAnterior([]);
      setCajaSeleccionadaId(null);
      setRendicionesExistentes({});
      setRendicionConfirmada(false);
      return;
    }
    setCargandoCajas(true);
    setErrorCajas(null);
    try {
      const params = new URLSearchParams({ sucursalId: String(sucursalSeleccionada.idsucursal) });
      const data = await requestJson<CajaDiaAnterior[]>(`/api/tesoreria/cajas-dia-anterior?${params.toString()}`);
      const lista = Array.isArray(data) ? data : [];
      setCajasDiaAnterior(lista);
      const ids = lista.map((caja) => Number(caja.idcontrolcaja)).filter((id) => Number.isFinite(id) && id > 0);
      if (ids.length) {
        const timestamp = Date.now().toString();
        try {
          const estados = await requestJson<RendicionExistente[]>(
            `/api/rendiciones/estados-cajas?ids=${ids.join(",")}&_=${timestamp}`,
          );
          const mapa = (Array.isArray(estados) ? estados : []).reduce<Record<number, RendicionExistente>>((acc, registro) => {
            if (Number.isFinite(registro.idcontrolcaja)) {
              acc[registro.idcontrolcaja] = registro;
            }
            return acc;
          }, {});
          setRendicionesExistentes(mapa);
          if (cajaSeleccionadaId && mapa[cajaSeleccionadaId]?.estado === "CONFIRMADA") {
            setRendicionConfirmada(true);
          } else {
            setRendicionConfirmada(false);
          }
        } catch (estadoError) {
          console.warn("No se pudieron obtener estados de rendiciones", estadoError);
          setRendicionesExistentes({});
          setRendicionConfirmada(false);
        }
      } else {
        setRendicionesExistentes({});
        setRendicionConfirmada(false);
      }
    } catch (error) {
      setErrorCajas(error instanceof Error ? error.message : "No se pudieron obtener las cajas del día anterior.");
      setRendicionesExistentes({});
      setRendicionConfirmada(false);
    } finally {
      setCargandoCajas(false);
    }
  }, [cajaSeleccionadaId, sucursalSeleccionada]);

  const cargarFormasPago = useCallback(async () => {
    setCargandoFormasPago(true);
    setErrorFormasPago(null);
    try {
      const data = await requestJson<FormaPagoDisponible[]>("/api/tesoreria/formas-pago");
      const filtradas = (Array.isArray(data) ? data : []).filter((forma) => {
        const descripcion = forma.descripcion?.trim().toUpperCase() ?? "";
        return forma.estado === 1 && !FORMAS_EXCLUIDAS.has(descripcion);
      });
      setFormasPago(filtradas);
    } catch (error) {
      setErrorFormasPago(error instanceof Error ? error.message : "No se pudieron cargar las formas de pago.");
    } finally {
      setCargandoFormasPago(false);
    }
  }, []);

  const limpiarValorFactura = (valor: string, max: number) => valor.replace(/[^0-9]/g, "").slice(0, max);

  const manejarFacturaRangoChange = useCallback(
    (rango: "desde" | "hasta", campo: keyof FacturaSegmento, valor: string) => {
      const normalizado = limpiarValorFactura(valor, campo === "numero" ? 7 : 3);
      if (rango === "desde") {
        setFacturaDesde((prev) => ({ ...prev, [campo]: normalizado }));
      } else {
        setFacturaHasta((prev) => ({ ...prev, [campo]: normalizado }));
      }
    },
    [],
  );

  const actualizarDigitoRango = useCallback(
    (
      rango: "desde" | "hasta",
      campo: keyof FacturaSegmento,
      indice: number,
      valor: string,
      max: number,
      indiceGlobal?: number,
      desplazamiento?: -1 | 1,
    ) => {
      const digito = valor.replace(/[^0-9]/g, "").slice(0, 1);
      const actual = (rango === "desde" ? facturaDesde : facturaHasta)[campo] ?? "";
      const chars = Array.from({ length: max }, (_, idx) => actual[idx] ?? "");
      chars[indice] = digito;
      manejarFacturaRangoChange(rango, campo, chars.join(""));
      if (typeof indiceGlobal === "number" && desplazamiento) {
        const destino = indiceGlobal + desplazamiento;
        if (destino >= 0 && destino < TOTAL_DIGITOS_FACTURA) {
          const ref = document.getElementById(
            construirIdFacturaInput(rango, destino),
          ) as HTMLInputElement | null;
          if (ref) {
            ref.select();
            ref.focus();
          }
        }
      }
    },
    [facturaDesde, facturaHasta, manejarFacturaRangoChange],
  );

  const manejarNuevaFacturaAnuladaChange = useCallback(
    (campo: keyof FacturaAnulada, valor: string) => {
      setNuevaFacturaAnulada((prev) => ({
        ...prev,
        [campo]:
          campo === "motivo"
            ? valor
            : limpiarValorFactura(valor, campo === "numero" ? 7 : 3),
      }));
    },
    [],
  );

  const agregarFacturaAnulada = useCallback(() => {
    if (
      !nuevaFacturaAnulada.establecimiento ||
      !nuevaFacturaAnulada.punto ||
      !nuevaFacturaAnulada.numero ||
      !nuevaFacturaAnulada.motivo.trim()
    ) {
      return;
    }
    setFacturasAnuladas((prev) => [
      ...prev,
      {
        id: nuevaFacturaAnulada.id || Date.now(),
        establecimiento: nuevaFacturaAnulada.establecimiento,
        punto: nuevaFacturaAnulada.punto,
        numero: nuevaFacturaAnulada.numero,
        motivo: nuevaFacturaAnulada.motivo.trim(),
      },
    ]);
    setNuevaFacturaAnulada({
      id: Date.now(),
      establecimiento: "",
      punto: "",
      numero: "",
      motivo: "",
    });
  }, [nuevaFacturaAnulada]);

  const eliminarFacturaAnulada = useCallback((id: number) => {
    setFacturasAnuladas((prev) => prev.filter((factura) => factura.id !== id));
  }, []);

  const editarFacturaAnulada = useCallback((factura: FacturaAnulada) => {
    setFacturasAnuladas((prev) => prev.filter((item) => item.id !== factura.id));
    setNuevaFacturaAnulada({
      id: factura.id,
      establecimiento: factura.establecimiento,
      punto: factura.punto,
      numero: factura.numero,
      motivo: factura.motivo,
    });
  }, []);

  useEffect(() => {
    void cargarSucursales();
    void cargarFormasPago();
  }, [cargarSucursales, cargarFormasPago]);

  useEffect(() => {
    void cargarCajas();
  }, [cargarCajas]);

  const cajaSeleccionada = useMemo(() => {
    if (cajaSeleccionadaId == null) {
      return null;
    }
    return (
      cajasDiaAnterior.find((caja) => Number(caja.idcontrolcaja) === Number(cajaSeleccionadaId)) ?? null
    );
  }, [cajaSeleccionadaId, cajasDiaAnterior]);

  const totalEfectivo = useMemo(() => {
    return DENOMINACIONES.reduce((acc, valor) => {
      return acc + valor * Number(denominaciones[valor] ?? "0");
    }, 0);
  }, [denominaciones]);

  const rendicionStorageKey = useMemo(() => {
    if (!sucursalSeleccionada || !cajaSeleccionada) {
      return null;
    }
    const sucursalId =
      sucursalSeleccionada.idsucursal ?? cajaSeleccionada.idsucursal ?? undefined;
    if (!sucursalId || !cajaSeleccionada.idcontrolcaja) {
      return null;
    }
    const turno = cajaSeleccionada.idturno ?? 0;
    return `rendicion:${sucursalId}-${cajaSeleccionada.idcontrolcaja}-${turno}`;
  }, [cajaSeleccionada, sucursalSeleccionada]);

  const totalGastos = useMemo(() => gastos.reduce((acc, gasto) => acc + (gasto.monto || 0), 0), [gastos]);
  const totalBoletas = useMemo(() => boletas.reduce((acc, boleta) => acc + (boleta.monto || 0), 0), [boletas]);
  const totalFormasPago = useMemo(() => {
    return formasPago.reduce((acc, forma) => {
      const valor = Number(pagosRegistrados[forma.id] ?? "0");
      return acc + (Number.isFinite(valor) ? valor : 0);
    }, 0);
  }, [formasPago, pagosRegistrados]);
  const rangoFacturasTexto = useMemo(() => {
    const inicio = formatearFacturaSegmento(facturaDesde);
    const fin = formatearFacturaSegmento(facturaHasta);
    if (inicio && fin) {
      return `${inicio} a ${fin}`;
    }
    return inicio ?? fin ?? "Sin declarar";
  }, [facturaDesde, facturaHasta]);

  const tieneRangoValido = useMemo(() => {
    const format = (segmento: FacturaSegmento) => {
      try {
        return formatearFacturaSegmento(segmento) ?? "";
      } catch {
        return "";
      }
    };
    return Boolean(format(facturaDesde) && format(facturaHasta));
  }, [facturaDesde, facturaHasta]);

  const generarVistaPreviaHtml = useCallback(() => {
    if (!sucursalSeleccionada || !cajaSeleccionada) {
      return null;
    }

    const formatearMoneda = (valor: number | string) =>
      `Gs. ${Number(valor || 0).toLocaleString("es-PY")}`;

    const numeroALetras = (num: number) => {
      const unidades = ["", "uno", "dos", "tres", "cuatro", "cinco", "seis", "siete", "ocho", "nueve"];
      const decenas = ["diez", "once", "doce", "trece", "catorce", "quince", "dieciséis", "diecisiete", "dieciocho", "diecinueve"];
      const decenasT = ["", "", "veinte", "treinta", "cuarenta", "cincuenta", "sesenta", "setenta", "ochenta", "noventa"];
      const centenas = ["", "cien", "doscientos", "trescientos", "cuatrocientos", "quinientos", "seiscientos", "setecientos", "ochocientos", "novecientos"];
      const entero = Math.floor(num);
      if (entero === 0) return "CERO GUARANÍES";
      const secciones: number[] = [];
      let valor = entero;
      while (valor > 0) {
        secciones.push(valor % 1000);
        valor = Math.floor(valor / 1000);
      }
      const etiquetas = ["", "MIL", "MILLONES", "MIL MILLONES"];
      const partes: string[] = [];
      secciones.forEach((seccion, indice) => {
        if (!seccion) return;
        const centenasNum = Math.floor(seccion / 100);
        const decenasNum = Math.floor((seccion % 100) / 10);
        const unidadesNum = seccion % 10;
        const texto: string[] = [];
        if (centenasNum) {
          if (centenasNum === 1 && (decenasNum || unidadesNum)) {
            texto.push("CIENTO");
          } else {
            texto.push(centenas[centenasNum].toUpperCase());
          }
        }
        if (decenasNum === 1) {
          texto.push(decenas[unidadesNum].toUpperCase());
        } else if (decenasNum > 1) {
          texto.push(decenasT[decenasNum].toUpperCase());
          if (unidadesNum) {
            texto.push(`Y ${unidades[unidadesNum].toUpperCase()}`);
          }
        } else if (unidadesNum) {
          texto.push((unidadesNum === 1 && indice > 0 ? "UN" : unidades[unidadesNum]).toUpperCase());
        }
        let bloque = texto.join(" ").trim();
        if (indice === 1 && seccion === 1) {
          bloque = "MIL";
        } else if (indice > 0) {
          bloque += ` ${etiquetas[indice]}`;
        }
        partes.unshift(bloque.trim());
      });
      return `${partes.join(" ").trim()} GUARANÍES`;
    };

    const fechaActual = new Date().toLocaleString("es-PY", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: "America/Asuncion",
    });

    const detalleDenominaciones =
      DENOMINACIONES.map((valor) => {
        const cantidad = Number(denominaciones[valor] ?? "0");
        if (!cantidad) {
          return "";
        }
        return `<div class="sub-row"><span>${cantidad} × ${formatearMoneda(valor)}</span><span>${formatearMoneda(
          valor * cantidad,
        )}</span></div>`;
      })
        .filter(Boolean)
        .join("") || '<div class="muted">Sin detalle de denominaciones.</div>';

    const formasPagoHtml =
      formasPago
        .map((forma) => ({
          descripcion: forma.descripcion,
          monto: Number(pagosRegistrados[forma.id] ?? "0"),
        }))
        .filter((entrada) => entrada.monto > 0)
        .map(
          (entrada) =>
            `<div class="sub-row"><span>${entrada.descripcion}</span><span>${formatearMoneda(entrada.monto)}</span></div>`,
        )
        .join("") || '<div class="muted">Sin montos declarados.</div>';

    const boletasHtml =
      boletas
        .map(
          (boleta) => `
            <div class="sub-row">
              <span>${boleta.numero ?? "-"} · ${boleta.banco ?? "-"} / ${boleta.cuenta ?? "-"}</span>
              <span>${formatearMoneda(boleta.monto ?? 0)}</span>
            </div>
          `,
        )
        .join("") || '<div class="muted">Aún no se cargaron boletas.</div>';

    const gastosHtml =
      gastos
        .map(
          (gasto) => `
          <div class="sub-row">
            <span>${gasto.numero ?? gasto.descripcion ?? "Gasto"}</span>
            <span>${formatearMoneda(gasto.monto ?? 0)}</span>
          </div>`,
        )
        .join("") || '<div class="muted">Sin comprobantes cargados.</div>';

    const rangoDesde = formatearFacturaSegmento(facturaDesde) ?? "-";
    const rangoHasta = formatearFacturaSegmento(facturaHasta) ?? "-";
    const declaracionNumerosHtml =
      !rangoDesde && !rangoHasta
        ? '<div class="muted">Sin declarar.</div>'
        : `<div class="sub-row"><span>Desde</span><span>${rangoDesde}</span></div>
           <div class="sub-row"><span>Hasta</span><span>${rangoHasta}</span></div>`;

    const facturasAnuladasHtml =
      facturasAnuladas.length === 0
        ? '<div class="muted">Sin registros.</div>'
        : facturasAnuladas
            .map((factura) => {
              const numero = formatearFacturaSegmento(factura) ?? "-";
              return `<div class="sub-row"><span>${numero}</span><span>${factura.motivo || ""}</span></div>`;
            })
            .join("");

    const observacionesHtml = notaFondos ? `<div class="muted small">${notaFondos}</div>` : '<div class="muted small">Sin comentarios.</div>';

    const baseUrl = typeof window !== "undefined" && window.location ? window.location.origin : "";
    const empresaNombre = sucursalSeleccionada.empresa?.nombre ?? "Empresa";
    const empresaRuc = sucursalSeleccionada.empresa?.ruc ?? sucursalSeleccionada.empresa?.codigo ?? "-";
    const empresaTelefono = sucursalSeleccionada.empresa?.telefono ?? "-";
    const empresaDireccion = sucursalSeleccionada.empresa?.direccion ?? "-";
    const normalizarEmpresa = (valor?: string | null) => (valor ?? "").trim().toUpperCase();
    const formatearFechaLarga = (input?: string | null) => {
      if (!input) {
        return "-";
      }
      const fecha = new Date(input);
      if (Number.isNaN(fecha.getTime())) {
        return input;
      }
      return fecha.toLocaleString("es-PY", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
        timeZone: "America/Asuncion",
      });
    };
    const empresaCodigo = normalizarEmpresa(
      sucursalSeleccionada?.empresa?.codigo || sucursalSeleccionada?.empresa?.nombre,
    );
    const seleccionarLogoEmpresa = () => {
      if (empresaCodigo.includes("GAMA")) {
        return `${baseUrl}/GAMA.svg`;
      }
      if (empresaCodigo.includes("PARLA")) {
        return `${baseUrl}/PARLA.svg`;
      }
      return `${baseUrl}/img/logo_bacon.svg`;
    };
    const logoEmpresa = seleccionarLogoEmpresa();
    const logoAplicacion = `${baseUrl}/baconappen.png`;
    const hostInfo = typeof window !== "undefined" ? window.location.hostname : "-";
    const urlActual = typeof window !== "undefined" ? window.location.href : "-";

    const html = `<!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8" />
      <title>Vista previa de rendición</title>
      <style>
      body{margin:0;background:#f0f0f0;font-family:Arial, sans-serif;}
      .ticket{width:76mm;margin:8px auto;background:#fff;color:#111;border:1px solid #ccc;padding:14px 6px 10px;}
      .logo-empresa{text-align:center;margin:10px 0 8px;}
      .logo-empresa img{max-width:30mm;width:50%;height:auto;object-fit:contain;}
      .company{text-align:center;font-size:10px;margin-bottom:6px;}
      .company-label{font-size:15.4px;font-weight:bold;text-transform:uppercase;}
      .company-meta{font-size:12.6px;}
      .sucursal{font-size:19.6px;font-weight:bold;text-align:center;text-transform:uppercase;margin-bottom:4px;}
      .title{font-size:16.8px;font-weight:bold;text-transform:uppercase;text-align:center;border-bottom:1px dashed #999;padding-bottom:4px;margin-bottom:2px;}
      .meta{font-size:14px;text-align:center;margin-bottom:4px;}
      .section{margin-top:6px;border-top:1px dashed #bbb;padding-top:4px;}
      .section-title{font-size:14px;font-weight:bold;text-transform:uppercase;margin-bottom:2px;}
      .row{display:flex;justify-content:space-between;font-size:14px;margin:2px 0;}
      .sub-table{padding-left:4px;border-left:2px solid #ddd;margin:2px 0;}
      .sub-row{display:flex;justify-content:space-between;font-size:12.6px;}
      .total-row{display:flex;justify-content:space-between;font-size:14px;font-weight:bold;margin-top:4px;border-top:1px dashed #bbb;padding-top:2px;}
      .muted{font-size:12.6px;color:#777;margin:2px 0;}
      .small{font-size:12.6px;}
      .signature{height:72px;border-top:1px solid #222;margin:0 0 4px;}
      .footer-meta{margin-top:12px;border-top:1px dashed #bbb;padding-top:8px;font-size:12.6px;text-align:center;}
      .footer-meta .footer-logo{margin-top:4px;}
      .footer-meta img{max-width:18mm;width:70%;height:auto;object-fit:contain;}
    </style>
    </head>
    <body>
      <div class="ticket">
        <div class="logo-empresa">
          <img src="${logoEmpresa}" alt="Logo de la empresa" />
        </div>
        <div class="company">
          <div class="company-label">${empresaNombre}</div>
          <div class="company-meta">RUC: ${empresaRuc}</div>
          <div class="company-meta">${empresaDireccion}</div>
          <div class="company-meta">Tel: ${empresaTelefono}</div>
        </div>
        <div class="sucursal">${(sucursalSeleccionada.sucursalNombre ?? cajaSeleccionada.sucursal ?? "-").toUpperCase()}</div>
        <div class="title">
          <div>Rendición de caja</div>
          <div>Caja: #${cajaSeleccionada.idcontrolcaja}</div>
        </div>
        <div class="meta">
          <div>Cajero/a: ${cajaSeleccionada.cajero ?? "-"}</div>
          <div>Turno: ${cajaSeleccionada.turno ?? "-"}</div>
        </div>
        <div class="meta small">Fecha/hora rendición: ${fechaActual}</div>

        <div class="section">
          <div class="section-title">Datos del turno</div>
          <div class="small">Turno: ${cajaSeleccionada.turno ?? "-"}</div>
          <div class="small">Caja: #${cajaSeleccionada.idcontrolcaja}</div>
          <div class="small">Cajero: ${cajaSeleccionada.cajero ?? "-"}</div>
          <div class="small">ID cajero: ${cajaSeleccionada.idcajero ?? "-"}</div>
          <div class="small">Apertura caja: ${formatearFechaLarga(cajaSeleccionada.fechaApertura)}</div>
          <div class="small">Cierre: ${formatearFechaLarga(cajaSeleccionada.fechaCierre)}</div>
          <div class="small">Estado turno: ${cajaSeleccionada.descEstadoTurno ?? "-"}</div>
          <div class="small">Estado caja: ${cajaSeleccionada.descEstadoCaja ?? "-"}</div>
        </div>

        <div class="section">
          <div class="section-title">Declaración de números</div>
          <div class="sub-table">${declaracionNumerosHtml}</div>
        </div>

        <div class="section">
          <div class="section-title">Facturas anuladas</div>
          <div class="sub-table">${facturasAnuladasHtml}</div>
        </div>

        <div class="section">
          <div class="section-title">Fondo para sencillo</div>
          <div class="sub-table">${detalleDenominaciones}</div>
          <div class="total-row"><span>Total fondo</span><span>${formatearMoneda(totalEfectivo)}</span></div>
          <div class="muted small">${numeroALetras(totalEfectivo)}</div>
        </div>

        <div class="section">
          <div class="section-title">Gastos declarados</div>
          <div class="sub-table">${gastosHtml}</div>
          <div class="total-row"><span>Total gastos</span><span>${formatearMoneda(totalGastos)}</span></div>
          <div class="muted small">${numeroALetras(totalGastos)}</div>
        </div>

        <div class="section">
          <div class="section-title">Formas de pago</div>
          <div class="sub-table">${formasPagoHtml}</div>
          <div class="total-row"><span>Total formas de pago</span><span>${formatearMoneda(totalFormasPago)}</span></div>
          <div class="muted small">${numeroALetras(totalFormasPago)}</div>
        </div>

        <div class="section">
          <div class="section-title">Boletas de depósito</div>
          <div class="sub-table">${boletasHtml}</div>
          <div class="total-row"><span>Total boletas</span><span>${formatearMoneda(totalBoletas)}</span></div>
          <div class="muted small">${numeroALetras(totalBoletas)}</div>
        </div>

        <div class="section">
          <div class="section-title">Observaciones</div>
          ${observacionesHtml}
        </div>

        <div class="section" style="margin-top:24px;border-top:1px dashed #bbb;padding-top:12px;">
          <div class="section-title" style="margin-bottom:0;">Firma</div>
          <div class="signature"></div>
          <div class="small">Nombre: ${cajaSeleccionada.cajero ?? "-"}</div>
          <div class="small">Fecha/hora firma: ${fechaActual}</div>
        </div>
        <div class="footer-meta">
          <div>Fue generado desde BaconApp el ${fechaActual} por ${usuarioActual?.nombre || "Usuario"} · CI ${
      usuarioActual?.documento || "-"
    }</div>
          <div>URL: ${urlActual}</div>
          <div>Host/IP: ${hostInfo}</div>
          <div class="footer-logo">
            <img src="${logoAplicacion}" alt="Logo BaconApp" />
          </div>
        </div>
      </div>
    </body>
    </html>`;

    return html;
  }, [
    boletas,
    cajaSeleccionada,
    denominaciones,
    facturasAnuladas,
    facturaDesde,
    facturaHasta,
    formasPago,
    gastos,
    notaFondos,
    pagosRegistrados,
    sucursalSeleccionada,
    totalBoletas,
    totalEfectivo,
    totalFormasPago,
    totalGastos,
    usuarioActual?.documento,
    usuarioActual?.nombre,
  ]);

  useEffect(() => {
    const html = generarVistaPreviaHtml();
    setVistaPreviaHtml(html);
  }, [generarVistaPreviaHtml]);

  const puedeAvanzar = useMemo(() => {
    if (pasoActualId === "seleccion") {
      return Boolean(sucursalSeleccionada && cajaSeleccionada);
    }
    if (pasoActualId === "facturas-rango") {
      return tieneRangoValido;
    }
    if (pasoActualId === "confirmacion") {
      return Boolean(resumen?.fecha);
    }
    return true;
  }, [pasoActualId, sucursalSeleccionada, cajaSeleccionada, resumen, tieneRangoValido]);

  const tieneCambios = useMemo(
    () =>
      Boolean(
        rendicionId ||
          sucursalSeleccionada ||
          cajaSeleccionada ||
          notaFondos.trim() ||
          gastos.length ||
          boletas.length ||
          Object.values(pagosRegistrados).some((valor) => Number(valor) > 0) ||
          facturasAnuladas.length ||
          formatearFacturaSegmento(facturaDesde) ||
          formatearFacturaSegmento(facturaHasta),
      ),
    [
      rendicionId,
      sucursalSeleccionada,
      cajaSeleccionada,
      notaFondos,
      gastos.length,
      boletas.length,
      pagosRegistrados,
      facturasAnuladas.length,
      facturaDesde,
      facturaHasta,
    ],
  );

  useEffect(() => {
    if (!tieneCambios) {
      return;
    }
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [tieneCambios]);

  useEffect(() => {
    if (!sucursalSeleccionada?.idsucursal) {
      setBancos([]);
      setCuentasBanco({});
      setCargandoCuentas({});
      setRendicionConfirmada(false);
      return;
    }
    setCargandoBancos(true);
    setBancos([]);
    setCuentasBanco({});
    setCargandoCuentas({});
    const sucursalId = sucursalSeleccionada.idsucursal;
    const timestamp = Date.now().toString();
    void requestJson<BancoReferencia[]>(`/api/rendiciones/bancos?sucursalId=${sucursalId}&_=${timestamp}`)
      .then((lista) => {
        const bancosNormalizados = Array.isArray(lista) ? lista : [];
        setBancos(bancosNormalizados);
        const cuentas: Record<number, CuentaBancoReferencia[]> = {};
        bancosNormalizados.forEach((banco) => {
          if (!banco?.id || !Array.isArray(banco.cuentas)) {
            return;
          }
          cuentas[banco.id] = banco.cuentas;
        });
        setCuentasBanco(cuentas);
      })
      .catch(() => {
        setBancos([]);
        setCuentasBanco({});
      })
      .finally(() => {
        setCargandoBancos(false);
      });
  }, [sucursalSeleccionada?.idsucursal]);

  const ensureRendicionId = useCallback(async () => {
    if (rendicionId) {
      return rendicionId;
    }
    if (!sucursalSeleccionada || !cajaSeleccionada) {
      throw new Error("Seleccioná una sucursal y caja para continuar.");
    }
    if (!cajaSeleccionada.idturno || !Number.isFinite(cajaSeleccionada.idturno)) {
      throw new Error("La caja seleccionada no tiene un turno válido.");
    }
    if (rendicionStorageKey) {
      try {
        const persistido = sessionStorage.getItem(rendicionStorageKey);
        if (persistido) {
          const idPersistido = Number(persistido);
          if (Number.isFinite(idPersistido) && idPersistido > 0) {
            setRendicionId(idPersistido);
            return idPersistido;
          }
        }
      } catch {
        /* ignorar errores de acceso al storage */
      }
    }
    const payload = {
      idsucursal: Number(sucursalSeleccionada.idsucursal),
      idcontrolcaja: Number(cajaSeleccionada.idcontrolcaja),
      idturno: Number(cajaSeleccionada.idturno),
      cajeroNombre: cajaSeleccionada.cajero ?? undefined,
      sucursalNombre:
        sucursalSeleccionada.sucursalNombre ??
        cajaSeleccionada.sucursalNombre ??
        cajaSeleccionada.sucursal ??
        undefined,
      empresaCodigo: sucursalSeleccionada.empresa?.codigo ?? undefined,
      empresaNombre: sucursalSeleccionada.empresa?.nombre ?? undefined,
    };
    const data = await requestJson<{ id: number }>("/api/rendiciones", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    setRendicionId(data.id);
    if (rendicionStorageKey) {
      try {
        sessionStorage.setItem(rendicionStorageKey, String(data.id));
      } catch {
        /* ignorar errores de storage */
      }
    }
    return data.id;
  }, [cajaSeleccionada, rendicionId, rendicionStorageKey, sucursalSeleccionada]);

  const guardarFondo = useCallback(async () => {
    const id = await ensureRendicionId();
    const items = DENOMINACIONES.map((valor) => ({
      valor,
      cantidad: Number(denominaciones[valor] ?? "0"),
    }));
    await requestJson(`/api/rendiciones/${id}/fondo`, {
      method: "PUT",
      body: JSON.stringify({ items }),
    });
  }, [denominaciones, ensureRendicionId]);

  const guardarGastos = useCallback(async () => {
    const id = await ensureRendicionId();
    const payload = gastos.map((gasto) => ({
      proveedor: gasto.proveedor || undefined,
      descripcion: gasto.descripcion,
      monto: gasto.monto,
      items: [],
    }));
    await requestJson(`/api/rendiciones/${id}/gastos`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  }, [ensureRendicionId, gastos]);

  const guardarFormasPago = useCallback(async () => {
    const id = await ensureRendicionId();
    const formas: { idFormaPago: number; descripcion: string; monto: number; nota?: string }[] = [];
    formasPago.forEach((forma) => {
      const monto = Number(pagosRegistrados[forma.id] ?? "0");
      const nota = (notasFormasPago[forma.id] ?? "").trim();
      if (!monto && !nota) {
        return;
      }
      formas.push({
        idFormaPago: forma.id,
        descripcion: forma.descripcion,
        monto,
        nota: nota || undefined,
      });
    });
    await requestJson(`/api/rendiciones/${id}/formas-pago`, {
      method: "PUT",
      body: JSON.stringify({ formas }),
    });
  }, [ensureRendicionId, formasPago, notasFormasPago, pagosRegistrados]);

  const guardarBoletas = useCallback(async () => {
    const id = await ensureRendicionId();
    const payload = boletas.map((boleta) => ({
      fecha: boleta.fecha || undefined,
      numero: boleta.numero || undefined,
      banco: boleta.banco || undefined,
      cuenta: boleta.cuenta || undefined,
      monto: boleta.monto,
    }));
    await requestJson(`/api/rendiciones/${id}/boletas`, {
      method: "PUT",
      body: JSON.stringify({ boletas: payload }),
    });
  }, [boletas, ensureRendicionId]);

  const guardarPasoActual = useCallback(async () => {
    if (
      !pasoActualId ||
      !["seleccion", "fondo", "gastos", "pagos", "boletas"].includes(pasoActualId)
    ) {
      return;
    }
    setErrorPaso(null);
    setGuardandoPaso(true);
    try {
      if (pasoActualId === "seleccion") {
        await ensureRendicionId();
      } else if (pasoActualId === "fondo") {
        await guardarFondo();
      } else if (pasoActualId === "gastos") {
        await guardarGastos();
      } else if (pasoActualId === "pagos") {
        await guardarFormasPago();
      } else if (pasoActualId === "boletas") {
        await guardarBoletas();
      }
    } catch (error) {
      setErrorPaso(error instanceof Error ? error.message : "No se pudo guardar este paso.");
      throw error;
    } finally {
      setGuardandoPaso(false);
    }
  }, [
    ensureRendicionId,
    guardarBoletas,
    guardarFormasPago,
    guardarFondo,
    guardarGastos,
    pasoActualId,
  ]);

  const registrarEvento = useCallback(
    async (accion: string, data?: { descripcion?: string; segundos?: number | null; intentos?: number | null; cambios?: Record<string, unknown> }) => {
      try {
        const id = await ensureRendicionId();
        const payload: Record<string, unknown> = { accion };
        if (data?.descripcion) {
          payload.descripcion = data.descripcion;
        }
        if (typeof data?.segundos === "number") {
          payload.segundosTranscurridos = data.segundos;
        }
        if (typeof data?.intentos === "number") {
          payload.intentosFallidos = data.intentos;
        }
        if (data?.cambios) {
          payload.cambios = data.cambios;
        }
        await requestJson(`/api/rendiciones/${id}/eventos`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      } catch (error) {
        console.warn("No se pudo registrar el evento de rendición:", accion, error);
      }
    },
    [ensureRendicionId],
  );

  const obtenerSegundosDesdeSeleccion = useCallback(() => {
    if (!inicioRendicionTimestamp) {
      return null;
    }
    return Math.floor((Date.now() - inicioRendicionTimestamp) / 1000);
  }, [inicioRendicionTimestamp]);

  const ejecutarAvancePaso = useCallback(async () => {
    try {
      await guardarPasoActual();
      setPasoActual((prev) => Math.min(prev + 1, WIZARD_STEPS.length - 1));
    } catch {
      /* el mensaje ya se manejó */
    }
  }, [guardarPasoActual]);

  const avanzarPaso = async () => {
    if (!puedeAvanzar || guardandoPaso || pasoActual === WIZARD_STEPS.length - 1) {
      return;
    }
    if (pasoActualId === "facturas-anuladas" && facturasAnuladas.length === 0) {
      const confirmar = window.confirm(
        "No cargaste ninguna factura anulada. ¿Querés continuar de todos modos?",
      );
      if (!confirmar) {
        return;
      }
    }
    if (pasoActualId === "pagos" && totalFormasPago <= 0) {
      setConfirmarPasoModal({
        visible: true,
        mensaje: "Aún no registraste ninguna forma de pago. ¿Querés avanzar sin declararlas?",
        confirmarTexto: "Continuar sin montos",
        onConfirm: () => {
          setConfirmarPasoModal({ visible: false, mensaje: "", confirmarTexto: undefined, onConfirm: null });
          void ejecutarAvancePaso();
        },
      });
      return;
    }
    if (pasoActualId === "fondo" && totalEfectivo <= 0) {
        setConfirmarPasoModal({
          visible: true,
          mensaje: "No registraste el detalle de sencillo. ¿Querés continuar sin cargarlo?",
          confirmarTexto: "Continuar sin cargar",
          onConfirm: () => {
            setConfirmarPasoModal({ visible: false, mensaje: "", confirmarTexto: undefined, onConfirm: null });
            void ejecutarAvancePaso();
          },
        });
      return;
    }
    if (pasoActualId === "boletas" && !avisoTemporizadorConfirmacionMostrado) {
      setConfirmarPasoModal({
        visible: true,
        mensaje:
          "Al entrar en la vista previa tendrás 90 segundos para confirmar la rendición. Pasado ese tiempo se reiniciará y deberás cargar todo nuevamente. ¿Deseás continuar?",
        confirmarTexto: "Sí, continuar",
        onConfirm: () => {
          setConfirmarPasoModal({ visible: false, mensaje: "", confirmarTexto: undefined, onConfirm: null });
          setAvisoTemporizadorConfirmacionMostrado(true);
          void ejecutarAvancePaso();
        },
      });
      return;
    }
    void ejecutarAvancePaso();
  };

  const retrocederPaso = () => {
    if (guardandoPaso || pasoActual === 0) {
      return;
    }
    setPasoActual((prev) => Math.max(prev - 1, 0));
    setErrorPaso(null);
  };

  const irAPaso = useCallback(
    (indice: number) => {
      if (guardandoPaso) {
        return;
      }
      if (indice < 0 || indice > maxPasoAlcanzado || indice === pasoActual) {
        return;
      }
      setPasoActual(indice);
      setErrorPaso(null);
    },
    [guardandoPaso, maxPasoAlcanzado, pasoActual],
  );

  const confirmarRendicion = async () => {
    try {
      await guardarBoletas();
      const id = await ensureRendicionId();
      setGuardandoConfirmacion(true);
      await requestJson(`/api/rendiciones/${id}/confirmar`, {
        method: "POST",
        body: JSON.stringify({ vistaPreviaHtml: vistaPreviaHtml ?? undefined }),
      });
      const fecha = new Date().toLocaleString("es-PY");
      setResumen({ mensaje: "Rendición confirmada y enviada.", fecha, tipo: "success" });
      setRendicionConfirmada(true);
      setConfirmacionTemporizadorActivo(false);
      setConfirmacionTiempoRestante(null);
      if (confirmacionTimerRef.current) {
        clearInterval(confirmacionTimerRef.current);
        confirmacionTimerRef.current = null;
      }
      void registrarEvento("rendicion_confirmada", {
        descripcion: "La rendición fue confirmada.",
        segundos: obtenerSegundosDesdeSeleccion() ?? undefined,
        intentos: confirmacionIntentosFallidos || undefined,
      });
      setModalExito(true);
      const indiceConfirmacion = obtenerIndicePaso("confirmacion");
      if (indiceConfirmacion >= 0) {
        setPasoActual(indiceConfirmacion);
      }
    } catch (error) {
      setRendicionConfirmada(false);
      setResumen({
        mensaje: error instanceof Error ? error.message : "No se pudo confirmar la rendición.",
        tipo: "danger",
      });
    } finally {
      setGuardandoConfirmacion(false);
    }
  };

  useEffect(() => {
    setNuevoGasto((prev) => {
      if (prev.articulos && prev.articulos.length > 0) {
        return prev;
      }
      return { ...prev, articulos: [crearArticuloGasto()] };
    });
  }, []);

  useEffect(() => {
    setMaxPasoAlcanzado((prev) => Math.max(prev, pasoActual));
  }, [pasoActual]);

  useEffect(() => {
    if (cajaSeleccionada && cajaSeleccionada.idcontrolcaja) {
      const idCaja = Number(cajaSeleccionada.idcontrolcaja);
      if (ultimaCajaRegistradaRef.current !== idCaja) {
        ultimaCajaRegistradaRef.current = idCaja;
        setInicioRendicionTimestamp(Date.now());
        setConfirmacionIntentosFallidos(0);
        setConfirmacionTiempoRestante(null);
        setConfirmacionTemporizadorActivo(false);
        setConfirmacionTimeoutModal(false);
        setAvisoTemporizadorConfirmacionMostrado(false);
        void registrarEvento("seleccion_caja", {
          descripcion: `Seleccionaste la caja #${idCaja}`,
          segundos: 0,
        });
      }
    } else {
      ultimaCajaRegistradaRef.current = null;
    }
  }, [
    cajaSeleccionada,
    registrarEvento,
    setConfirmacionIntentosFallidos,
    setConfirmacionTiempoRestante,
    setConfirmacionTemporizadorActivo,
    setConfirmacionTimeoutModal,
    setAvisoTemporizadorConfirmacionMostrado,
  ]);

  useEffect(() => {
    if (
      pasoActualId === "confirmacion" &&
      !rendicionConfirmada &&
      !confirmacionTemporizadorActivo
    ) {
      setConfirmacionTiempoRestante((prev) => (prev == null ? 90 : prev));
      setConfirmacionTemporizadorActivo(true);
    }
  }, [pasoActualId, rendicionConfirmada, confirmacionTemporizadorActivo]);

  useEffect(() => {
    if (confirmacionTemporizadorActivo && !rendicionConfirmada) {
      if (confirmacionTimerRef.current) {
        return;
      }
      confirmacionTimerRef.current = setInterval(() => {
        setConfirmacionTiempoRestante((prev) => {
          if (prev == null) {
            return prev;
          }
          if (prev <= 1) {
            if (confirmacionTimerRef.current) {
              clearInterval(confirmacionTimerRef.current);
              confirmacionTimerRef.current = null;
            }
            setConfirmacionTemporizadorActivo(false);
            setConfirmacionTiempoRestante(0);
            setConfirmacionTimeoutModal(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => {
        if (confirmacionTimerRef.current) {
          clearInterval(confirmacionTimerRef.current);
          confirmacionTimerRef.current = null;
        }
      };
    }
    if (confirmacionTimerRef.current) {
      clearInterval(confirmacionTimerRef.current);
      confirmacionTimerRef.current = null;
    }
  }, [confirmacionTemporizadorActivo, rendicionConfirmada]);

  const agregarGasto = () => {
    if (!nuevoGasto.descripcion.trim()) {
      return;
    }
    const articulosNormalizados = (nuevoGasto.articulos ?? []).map((item) => ({
      descripcion: item.descripcion ?? "",
      codigo: item.codigo ?? "",
      articulo: item.articulo ?? "",
      unidad: item.unidad ?? "UNI",
      cantidad: Number(item.cantidad ?? 0),
      precioUnitario: Number(item.precioUnitario ?? 0),
      monto: Number(item.monto ?? 0),
      iva: Number(item.iva ?? 0),
    }));
    const subtotal = articulosNormalizados.reduce((acc, item) => {
      if (item.monto > 0) {
        return acc + item.monto;
      }
      return acc + item.cantidad * item.precioUnitario;
    }, 0);
    const totalIva = articulosNormalizados.reduce((acc, item) => acc + item.iva, 0);
    const montoTotal = articulosNormalizados.length > 0 ? subtotal + totalIva : nuevoGasto.monto;

    setGastos((prev) => [
      ...prev,
      {
        ...nuevoGasto,
        id: Date.now(),
        monto: montoTotal,
        articulos: articulosNormalizados,
      },
    ]);
    setNuevoGasto(() => {
      const base = crearGastoBase();
      return {
        ...base,
        tipo: nuevoGasto.tipo,
        compraTipo: nuevoGasto.compraTipo,
      };
    });
  };

  const eliminarGasto = (id: number) => {
    setGastos((prev) => prev.filter((gasto) => gasto.id !== id));
  };

  const actualizarArticuloGasto = (index: number, campo: keyof GastoItemForm, valor: string | number) => {
    setNuevoGasto((prev) => {
      const articulos = prev.articulos ? [...prev.articulos] : [crearArticuloGasto()];
      articulos[index] = {
        ...articulos[index],
        [campo]:
          campo === "descripcion" || campo === "codigo" || campo === "articulo" || campo === "unidad"
            ? String(valor)
            : Number(valor) || 0,
      };
      return { ...prev, articulos };
    });
  };

  const agregarArticuloGasto = () => {
    setNuevoGasto((prev) => ({
      ...prev,
      articulos: [...(prev.articulos ?? []), crearArticuloGasto()],
    }));
  };

  const eliminarArticuloGasto = (index: number) => {
    setNuevoGasto((prev) => {
      const articulos = prev.articulos ? [...prev.articulos] : [];
      if (articulos.length <= 1) {
        articulos[0] = crearArticuloGasto();
      } else {
        articulos.splice(index, 1);
      }
      return { ...prev, articulos };
    });
  };

  const agregarBoleta = () => {
    const numero = nuevaBoleta.numero.trim();
    const camposCompletos =
      Boolean(nuevaBoleta.fecha) &&
      Boolean(numero) &&
      Boolean(nuevaBoleta.banco) &&
      Boolean(nuevaBoleta.cuenta) &&
      nuevaBoleta.monto > 0;
    if (!camposCompletos) {
      setErrorPaso("Completá todos los campos de la boleta antes de guardarla.");
      return;
    }
    setErrorPaso(null);
    setBoletas((prev) => [...prev, { ...nuevaBoleta, id: Date.now() }]);
    setNuevaBoleta({ id: Date.now(), fecha: "", numero: "", banco: "", cuenta: "", monto: 0 });
  };

  const eliminarBoleta = (id: number) => {
    setBoletas((prev) => prev.filter((boleta) => boleta.id !== id));
  };

  const manejarSeleccionSucursal = (sucursal: SucursalRendicion) => {
    if (sucursalSeleccionada?.idsucursal === sucursal.idsucursal) {
      return;
    }
    setSucursalSeleccionada(sucursal);
    setCajaSeleccionadaId(null);
    reiniciarFlujo();
  };

  const manejarSeleccionSucursalPorValor = (valor: string) => {
    if (!valor) {
      setSucursalSeleccionada(null);
      setCajaSeleccionadaId(null);
      setCajasDiaAnterior([]);
      reiniciarFlujo();
      return;
    }
    const siguiente = sucursales.find(
      (registro) => construirClaveSucursal(registro) === valor,
    );
    if (siguiente) {
      manejarSeleccionSucursal(siguiente);
    }
  };

  const aplicarSeleccionCaja = (caja: CajaDiaAnterior) => {
    const cajaId = Number(caja.idcontrolcaja);
    setCajaSeleccionadaId(cajaId);
    reiniciarFlujo({ conservarBancos: true });
    const rendicion = rendicionesExistentes[cajaId];
    if (rendicion) {
      setRendicionId(rendicion.rendicionId);
      setRendicionConfirmada(rendicion.estado?.toUpperCase() === "CONFIRMADA");
    } else {
      setRendicionId(null);
      setRendicionConfirmada(false);
    }
  };

  const manejarSeleccionCaja = (cajaId: number) => {
    if (cajaSeleccionadaId === cajaId) {
      return;
    }
    const cajaDestino = cajasDiaAnterior.find((caja) => Number(caja.idcontrolcaja) === Number(cajaId));
    if (!cajaDestino) {
      return;
    }
    const usuarioNombre = usuarioActual?.nombre ?? "";
    const cajeroAsignado = cajaDestino.cajero ?? "";
    if (usuarioNombre && cajeroAsignado) {
      const normalizar = (valor: string) =>
        valor
          .toUpperCase()
          .trim()
          .split(/\s+/)
          .filter((token) => token.length >= 3);
      const tokensUsuario = normalizar(usuarioNombre);
      const tokensCajero = normalizar(cajeroAsignado);
      const textoUsuario = usuarioNombre.toUpperCase();
      const textoCajero = cajeroAsignado.toUpperCase();
      const coincideNombre =
        tokensUsuario.some((token) => textoCajero.includes(token)) ||
        tokensCajero.some((token) => textoUsuario.includes(token));
      if (!coincideNombre) {
        setConfirmarCajaModal({
          visible: true,
          caja: cajaDestino,
          mensaje: `La caja seleccionada está asignada a ${cajeroAsignado}, pero tu usuario es ${usuarioNombre}. ¿Deseás continuar?`,
        });
        return;
      }
    }
    aplicarSeleccionCaja(cajaDestino);
  };

  const manejarImprimir = useCallback(() => {
    if (!resumen?.fecha) {
      setErrorPaso("Confirmá la rendición antes de imprimir.");
      return;
    }
    window.print();
  }, [resumen?.fecha]);

  const manejarVerVistaPrevia = useCallback(() => {
    if (!vistaPreviaHtml) {
      manejarImprimir();
      return;
    }
    const ventana = window.open("", "_blank", "width=900,height=800");
    if (!ventana) {
      return;
    }
    ventana.document.write(vistaPreviaHtml);
    ventana.document.close();
    ventana.focus();
  }, [vistaPreviaHtml, manejarImprimir]);

  const cerrarModalConfirmarCaja = () => {
    setConfirmarCajaModal({ visible: false, caja: null, mensaje: "" });
  };

  const confirmarSeleccionCajaAsignada = () => {
    if (!confirmarCajaModal.caja) {
      cerrarModalConfirmarCaja();
      return;
    }
    const caja = confirmarCajaModal.caja;
    cerrarModalConfirmarCaja();
    aplicarSeleccionCaja(caja);
  };

  const cerrarConfirmarPasoModal = () => {
    setConfirmarPasoModal({ visible: false, mensaje: "", confirmarTexto: undefined, onConfirm: null });
  };

  const ejecutarConfirmarPasoModal = () => {
    if (confirmarPasoModal.onConfirm) {
      confirmarPasoModal.onConfirm();
    } else {
      cerrarConfirmarPasoModal();
    }
  };

  const manejarTimeoutConfirmacion = () => {
    setConfirmacionTimeoutModal(false);
    setConfirmacionTemporizadorActivo(false);
    if (confirmacionTimerRef.current) {
      clearInterval(confirmacionTimerRef.current);
      confirmacionTimerRef.current = null;
    }
    const intentosActualizados = confirmacionIntentosFallidos + 1;
    setConfirmacionIntentosFallidos(intentosActualizados);
    void registrarEvento("rendicion_timeout", {
      descripcion: "El tiempo de confirmación expiró.",
      segundos: obtenerSegundosDesdeSeleccion() ?? undefined,
      intentos: intentosActualizados,
    });
    reiniciarFlujo();
  };

  const formatearTiempoRestante = (valor?: number | null) => {
    if (valor == null || valor <= 0) {
      return null;
    }
    const minutos = Math.floor(valor / 60);
    const segundos = valor % 60;
    return `${String(minutos).padStart(2, "0")}:${String(segundos).padStart(2, "0")}`;
  };

  const tiempoRestanteGlobal = formatearTiempoRestante(confirmacionTiempoRestante);

  const manejarImprimirVistaPrevia = useCallback(() => {
    if (!vistaPreviaHtml) {
      manejarImprimir();
      return;
    }
    const ventana = window.open("", "_blank", "width=900,height=800");
    if (!ventana) {
      return;
    }
    ventana.document.write(vistaPreviaHtml);
    ventana.document.close();
    ventana.focus();
    ventana.print();
  }, [vistaPreviaHtml, manejarImprimir]);

  const manejarCambioDenominacion = useCallback((valor: number, cantidad: string) => {
    setDenominaciones((prev) => ({
      ...prev,
      [valor]: cantidad,
    }));
  }, []);

  const manejarCambioNuevoGasto = useCallback((campo: keyof GastoForm, valor: string | number) => {
    setNuevoGasto((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  }, []);

  const manejarCambioNuevaBoleta = useCallback((campo: keyof BoletaForm, valor: string | number) => {
    setNuevaBoleta((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  }, []);

  const manejarCambioBancoBoleta = useCallback(
    (bancoId: number) => {
      setNuevaBoleta((prev) => ({ ...prev, banco: bancoId ? String(bancoId) : "", cuenta: "" }));
      if (!bancoId) {
        return;
      }
      if (cuentasBanco[bancoId]) {
        return;
      }
      const bancoSeleccionado = bancos.find((banco) => banco.id === bancoId);
      if (bancoSeleccionado?.cuentas) {
        setCuentasBanco((prev) => ({ ...prev, [bancoId]: bancoSeleccionado.cuentas ?? [] }));
        return;
      }
      if (!sucursalSeleccionada?.idsucursal) {
        setCuentasBanco((prev) => ({ ...prev, [bancoId]: [] }));
        return;
      }
      setCargandoCuentas((prev) => ({ ...prev, [bancoId]: true }));
      const timestamp = Date.now().toString();
      void requestJson<CuentaBancoReferencia[]>(
        `/api/rendiciones/bancos/${bancoId}/cuentas?sucursalId=${sucursalSeleccionada.idsucursal}&_=${timestamp}`,
      )
        .then((cuentas) => {
          setCuentasBanco((prev) => ({ ...prev, [bancoId]: cuentas ?? [] }));
        })
        .catch(() => {
          setCuentasBanco((prev) => ({ ...prev, [bancoId]: [] }));
        })
        .finally(() => {
          setCargandoCuentas((prev) => ({ ...prev, [bancoId]: false }));
        });
    },
    [bancos, cuentasBanco, sucursalSeleccionada?.idsucursal],
  );

  const manejarMontoFormaPagoChange = useCallback((id: number, valor: string) => {
    setPagosRegistrados((prev) => ({
      ...prev,
      [id]: valor,
    }));
  }, []);

  const manejarNotaFormaPagoChange = useCallback((id: number, valor: string) => {
    setNotasFormasPago((prev) => ({
      ...prev,
      [id]: valor,
    }));
  }, []);

  const abrirModalEfectivo = useCallback((forma: FormaPagoDisponible) => {
    setModalEfectivo({ abierta: true, forma });
  }, []);

  const cerrarModalEfectivo = useCallback(() => {
    setModalEfectivo({ abierta: false, forma: null });
  }, []);

  const manejarDetalleEfectivoModalChange = useCallback(
    (denominacion: number, valor: string) => {
      if (!modalEfectivo.forma) {
        return;
      }
      const formaId = modalEfectivo.forma.id;
      setDetalleEfectivoFormas((prev) => ({
        ...prev,
        [formaId]: {
          ...(prev[formaId] ?? {}),
          [denominacion]: valor,
        },
      }));
    },
    [modalEfectivo.forma],
  );

  const guardarDetalleEfectivo = useCallback(() => {
    if (!modalEfectivo.forma) {
      cerrarModalEfectivo();
      return;
    }
    const formaId = modalEfectivo.forma.id;
    const detalle = detalleEfectivoFormas[formaId] ?? {};
    const total = DENOMINACIONES.reduce(
      (acc, denominacion) => acc + denominacion * Number(detalle[denominacion] ?? "0"),
      0,
    );
    setPagosRegistrados((prev) => ({
      ...prev,
      [formaId]: total > 0 ? String(total) : "",
    }));
    cerrarModalEfectivo();
  }, [cerrarModalEfectivo, detalleEfectivoFormas, modalEfectivo.forma]);

  const abrirModalTarjeta = useCallback((forma: FormaPagoDisponible, tipo: "credito" | "debito") => {
    setModalTarjeta({ abierta: true, forma, tipo });
  }, []);

  const cerrarModalTarjeta = useCallback(() => {
    setModalTarjeta({ abierta: false, forma: null, tipo: null });
  }, []);

  const manejarDetalleTarjetaModalChange = useCallback(
    (proveedor: string, valor: string) => {
      if (!modalTarjeta.forma) {
        return;
      }
      const formaId = modalTarjeta.forma.id;
      setDetalleTarjetaFormas((prev) => ({
        ...prev,
        [formaId]: {
          ...(prev[formaId] ?? {}),
          [proveedor]: valor,
        },
      }));
    },
    [modalTarjeta.forma],
  );

  const guardarDetalleTarjeta = useCallback(() => {
    if (!modalTarjeta.forma) {
      cerrarModalTarjeta();
      return;
    }
    const formaId = modalTarjeta.forma.id;
    const detalle = detalleTarjetaFormas[formaId] ?? {};
    const total = DETALLE_TARJETA_PROVEEDORES.reduce(
      (acc, proveedor) => acc + Number(detalle[proveedor] ?? "0"),
      0,
    );
    setPagosRegistrados((prev) => ({
      ...prev,
      [formaId]: total > 0 ? String(total) : "",
    }));
    cerrarModalTarjeta();
  }, [cerrarModalTarjeta, detalleTarjetaFormas, modalTarjeta.forma]);

  const stepLayoutProps = {
    onBack: retrocederPaso,
    onNext: avanzarPaso,
    disableBack: pasoActual === 0 || guardandoPaso,
    disableNext:
      pasoActual === WIZARD_STEPS.length - 1 ||
      !puedeAvanzar ||
      guardandoPaso ||
      guardandoConfirmacion ||
      rendicionConfirmada,
    isNextLoading: guardandoPaso,
  };

  const confirmacionExtraControls = useMemo(() => {
    if (!resumen?.fecha) {
      return null;
    }
    return (
      <>
        {vistaPreviaHtml && manejarVerVistaPrevia && (
          <Button color="primary" size="sm" onClick={manejarVerVistaPrevia}>
            Ver vista previa
          </Button>
        )}
        <Button color="success" size="sm" onClick={manejarImprimirVistaPrevia}>
          Imprimir / guardar PDF
        </Button>
        <Button color="light" size="sm" onClick={() => setPasoActual(0)}>
          Iniciar nueva rendición
        </Button>
      </>
    );
  }, [resumen?.fecha, vistaPreviaHtml, manejarVerVistaPrevia, manejarImprimirVistaPrevia, setPasoActual]);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Rendición de caja"
          pageTitle="Módulos"
          description="Completá los pasos para seleccionar la caja, registrar movimientos y confirmar la rendición."
        />
        <StepArrowNav steps={timelineSteps} current={pasoActual} onStepClick={irAPaso} />
        <Card>
          <CardBody>
            {tiempoRestanteGlobal && (
              <Alert color="danger" className="mb-3">
                Tenés <strong>{tiempoRestanteGlobal}</strong> para confirmar antes de reiniciar la rendición.
              </Alert>
            )}
            {errorPaso && (
              <Alert color="danger" className="mb-3">
                {errorPaso}
              </Alert>
            )}

            <div className="pt-2">
              {pasoActualId === "seleccion" && (
                <StepLayout
                  title="Seleccionar caja"
                  description="Elegí la sucursal y la caja del turno anterior que vas a rendir."
                  {...stepLayoutProps}
                >
                  <SeleccionStep
                    sucursales={sucursales}
                    sucursalSeleccionada={sucursalSeleccionada}
                    cajasDiaAnterior={cajasDiaAnterior}
                    cajaSeleccionadaId={cajaSeleccionadaId}
                    rendicionesExistentes={rendicionesExistentes}
                    onSucursalChange={manejarSeleccionSucursalPorValor}
                    onCajaSelect={manejarSeleccionCaja}
                    onReloadCajas={() => {
                      void cargarCajas();
                    }}
                    errorSucursales={errorSucursales}
                    errorCajas={errorCajas}
                    cargandoSucursales={cargandoSucursales}
                    cargandoCajas={cargandoCajas}
                    intentosFallidos={confirmacionIntentosFallidos}
                />
              </StepLayout>
              )}

              {pasoActualId === "facturas-rango" && (
                <StepLayout
                  title="Declarar números de factura"
                  description="Ingresá el rango fiscal que se utilizó en esta rendición."
                  {...stepLayoutProps}
                >
                  <FacturasRangoStep
                    facturaDesde={facturaDesde}
                    facturaHasta={facturaHasta}
                    puedeEditar={Boolean(sucursalSeleccionada && cajaSeleccionada)}
                    tieneRangoValido={tieneRangoValido}
                    onDigitChange={actualizarDigitoRango}
                  />
                </StepLayout>
              )}

              {pasoActualId === "facturas-anuladas" && (
                <StepLayout
                  title="Declaración de facturas anuladas"
                  description="Registrá las facturas anuladas y el motivo correspondiente."
                  {...stepLayoutProps}
                >
                  <FacturasAnuladasStep
                    nuevaFacturaAnulada={nuevaFacturaAnulada}
                    facturasAnuladas={facturasAnuladas}
                    puedeEditar={Boolean(sucursalSeleccionada && cajaSeleccionada)}
                    onNuevaFacturaChange={manejarNuevaFacturaAnuladaChange}
                    onAgregarFactura={agregarFacturaAnulada}
                    onEliminarFactura={eliminarFacturaAnulada}
                    onEditarFactura={editarFacturaAnulada}
                  />
                </StepLayout>
              )}

              {pasoActualId === "fondo" && (
                <StepLayout
                  title="Fondo para sencillo"
                  description="Desglosá el efectivo disponible y registrá notas internas antes de continuar."
                  {...stepLayoutProps}
                >
                  <FondoStep
                    denominaciones={denominaciones}
                    totalEfectivo={totalEfectivo}
                    notaFondos={notaFondos}
                    onDenominacionChange={manejarCambioDenominacion}
                    onNotaChange={setNotaFondos}
                  />
                </StepLayout>
              )}

              {pasoActualId === "gastos" && (
                <StepLayout
                  title="Gastos del turno"
                  description="Repasá lo que ya registraste y cargá nuevos comprobantes cuando sea necesario."
                  {...stepLayoutProps}
                >
                  <GastosStep
                    nuevoGasto={nuevoGasto}
                    gastos={gastos}
                    totalGastos={totalGastos}
                    onNuevoGastoChange={manejarCambioNuevoGasto}
                    onAgregarGasto={agregarGasto}
                    onEliminarGasto={eliminarGasto}
                    onAgregarItem={agregarArticuloGasto}
                    onEliminarItem={eliminarArticuloGasto}
                    onActualizarItem={actualizarArticuloGasto}
                  />
                </StepLayout>
              )}

              {pasoActualId === "pagos" && (
                <StepLayout
                  title="Formas de pago"
                  description="Declarar los montos cobrados por cada medio."
                  {...stepLayoutProps}
                >
                  <PagosStep
                    formasPago={formasPago}
                    pagosRegistrados={pagosRegistrados}
                    notasFormasPago={notasFormasPago}
                    errorFormasPago={errorFormasPago}
                    cargandoFormasPago={cargandoFormasPago}
                    totalFormasPago={totalFormasPago}
                    onMontoChange={manejarMontoFormaPagoChange}
                    onNotaChange={manejarNotaFormaPagoChange}
                    onAbrirEfectivo={abrirModalEfectivo}
                    onAbrirTarjeta={abrirModalTarjeta}
                    detalleEfectivo={detalleEfectivoFormas}
                    detalleTarjeta={detalleTarjetaFormas}
                  />
                </StepLayout>
              )}

              {pasoActualId === "boletas" && (
                <StepLayout
                  title="Boletas de depósito"
                  description="Carga los datos de las boletas bancarias presentadas."
                  {...stepLayoutProps}
                >
                  <BoletasStep
                    nuevaBoleta={nuevaBoleta}
                    boletas={boletas}
                    totalBoletas={totalBoletas}
                    onNuevaBoletaChange={manejarCambioNuevaBoleta}
                    onAgregarBoleta={agregarBoleta}
                    onEliminarBoleta={eliminarBoleta}
                    bancos={bancos}
                    cuentas={cuentasBanco}
                    cargandoBancos={cargandoBancos}
                    cargandoCuentaIds={cargandoCuentas}
                    onBancoChange={manejarCambioBancoBoleta}
                  />
                </StepLayout>
              )}

              {pasoActualId === "confirmacion" && (
                <StepLayout
                  title="Resumen y confirmación"
                  description="Verificá los totales antes de confirmar la rendición."
                  {...stepLayoutProps}
                  onNext={confirmarRendicion}
                  nextLabel={rendicionConfirmada ? "Rendición confirmada" : "Confirmar rendición"}
                  loadingLabel="Confirmando..."
                  disableNext={
                    guardandoConfirmacion ||
                    rendicionConfirmada ||
                    confirmacionTiempoRestante === 0 ||
                    !sucursalSeleccionada ||
                    !cajaSeleccionada
                  }
                  isNextLoading={guardandoConfirmacion}
                  extraControls={confirmacionExtraControls}
                >
                  <ConfirmacionStep
                    sucursalSeleccionada={sucursalSeleccionada}
                    cajaSeleccionada={cajaSeleccionada}
                    totalEfectivo={totalEfectivo}
                    totalGastos={totalGastos}
                    totalFormasPago={totalFormasPago}
                    totalBoletas={totalBoletas}
                    rangoFacturasTexto={rangoFacturasTexto}
                    facturasAnuladas={facturasAnuladas}
                    notaFondos={notaFondos}
                    resumen={resumen}
                    vistaPreviaHtml={vistaPreviaHtml}
                    tiempoRestanteConfirmacion={confirmacionTiempoRestante}
                  />
                </StepLayout>
              )}
            </div>
          </CardBody>
        </Card>

        <EfectivoDetalleModal
          isOpen={modalEfectivo.abierta}
          forma={modalEfectivo.forma}
          detalle={detalleEfectivoSeleccionado}
          totalCalculado={totalDetalleEfectivoSeleccionado}
          onChange={manejarDetalleEfectivoModalChange}
          onClose={cerrarModalEfectivo}
          onSave={guardarDetalleEfectivo}
        />

        <TarjetaDetalleModal
          isOpen={modalTarjeta.abierta}
          forma={modalTarjeta.forma}
          tipo={modalTarjeta.tipo}
          detalle={detalleTarjetaSeleccionado}
          totalCalculado={totalDetalleTarjetaSeleccionado}
          onChange={manejarDetalleTarjetaModalChange}
          onClose={cerrarModalTarjeta}
          onSave={guardarDetalleTarjeta}
        />

        <Modal isOpen={modalExito} toggle={() => setModalExito(false)} centered>
          <ModalHeader toggle={() => setModalExito(false)}>Rendición enviada</ModalHeader>
          <ModalBody>
            <p className="mb-0">La rendición fue confirmada y enviada a tesorería. Podés continuar con la impresión.</p>
          </ModalBody>
          <ModalFooter>
            <Button color="success" onClick={() => setModalExito(false)}>
              Entendido
            </Button>
          </ModalFooter>
        </Modal>

        <Modal isOpen={confirmarCajaModal.visible} toggle={cerrarModalConfirmarCaja} centered>
          <ModalHeader toggle={cerrarModalConfirmarCaja}>Caja asignada a otro usuario</ModalHeader>
          <ModalBody>
            <p className="mb-0">{confirmarCajaModal.mensaje || "¿Querés continuar con esta caja?"}</p>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={cerrarModalConfirmarCaja}>
              Cancelar
            </Button>
            <Button color="warning" onClick={confirmarSeleccionCajaAsignada}>
              Sí, continuar
            </Button>
          </ModalFooter>
        </Modal>

        <Modal isOpen={confirmarPasoModal.visible} toggle={cerrarConfirmarPasoModal} centered>
          <ModalHeader toggle={cerrarConfirmarPasoModal}>Confirmar acción</ModalHeader>
          <ModalBody>
            <p className="mb-0">{confirmarPasoModal.mensaje}</p>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={cerrarConfirmarPasoModal}>
              Cancelar
            </Button>
            <Button color="warning" onClick={ejecutarConfirmarPasoModal}>
              {confirmarPasoModal.confirmarTexto ?? "Continuar"}
            </Button>
          </ModalFooter>
        </Modal>

        <Modal isOpen={confirmacionTimeoutModal} toggle={() => {}} centered>
          <ModalHeader>Tiempo agotado</ModalHeader>
          <ModalBody>
            <p className="mb-0">
              Pasaron más de 90 segundos sin confirmar la rendición. Debés reiniciar el proceso para garantizar la
              seguridad de la caja.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={manejarTimeoutConfirmacion}>
              Reiniciar rendición
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};
export default RendicionCajaPage;
